import { onlineManager } from "@tanstack/react-query";
import { globalNotify } from "common/utils/notificationBus";

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Writes allowed offline even though they target our API:
// - token endpoints are background auth (a blocked refresh must not notify).
const OFFLINE_ALLOWED_PATHS = ["/v1/tokens"];

// Background writes rejected offline without a user-facing toast (they retry
// when connectivity returns or are fire-and-forget telemetry).
const OFFLINE_SILENT_PATHS = ["/v1/engagement-events"];

let installed = false;

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (input instanceof Request) return input.method.toUpperCase();
  return "GET";
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

export const OFFLINE_WRITE_BLOCKED_MESSAGE =
  "You are offline. This action requires a connection.";

export function isOfflineWriteBlockedError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    error.message === OFFLINE_WRITE_BLOCKED_MESSAGE
  );
}

/**
 * Single network choke point. Wraps window.fetch once so EVERY write request to
 * our API (any client or raw fetch) is refused while the device is offline.
 * A fetch added anywhere cannot bypass this. User-initiated writes show a toast;
 * background telemetry is rejected silently. Reads (GET) and third-party requests
 * are left alone, so offline screens still read from the persisted cache. Install
 * once at app start, before anything fetches.
 */
export function installOfflineWriteGuard(): void {
  if (installed) return;
  installed = true;

  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT ?? "";
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!onlineManager.isOnline() && WRITE_METHODS.has(methodOf(input, init))) {
      const url = urlOf(input);
      const isOurApi = apiEndpoint !== "" && url.startsWith(apiEndpoint);
      const isAllowed = OFFLINE_ALLOWED_PATHS.some((p) => url.includes(p));
      const isSilent = OFFLINE_SILENT_PATHS.some((p) => url.includes(p));
      if (isOurApi && !isAllowed) {
        if (!isSilent) {
          globalNotify("You need to be online to use this.", "error");
        }
        return Promise.reject(new TypeError(OFFLINE_WRITE_BLOCKED_MESSAGE));
      }
    }
    return originalFetch(input, init);
  };
}
